
export interface CommissionProfile {
    id: number;
    createdAtUtc: Date;
    updatedAtUtc: Date;
    createdBy: string;
    overAll?: Commission;
    additionalFees?: Commission;
    heating?: Commission;
    typeOnly: Commission[];
    methods: Commission[];
}

export interface Commission {
    type: string;
    value: number;
    unit: CommissionUnit;
    typeKey: string;
    methodKey?: string;
    maxDimensions: string | null;
    maxSizeCM2: number | null;
    maxSize: number | null;
    /**
     * NAV price group of the method this rule targets ("Standard", "CM2",
     * "Type 2 CM2", "Type 3 CM2" - see API KitMaker.Model.Enums.PriceTypes).
     * The API's PriceAdjustmentService.MaxSizeIsBelowLogoSize branches on this
     * per rule: STANDARD rules match on maxDimensions "HxW", CM2 rules ONLY on
     * maxSizeCM2. Sent on save so cm2 size overrides keep the correct branch.
     */
    priceType?: string | null;
}

// WARN: We use DKK here, but in reality the options are "percentage" and "amount".
//  We keep the enum as-is to not break the anything right now, as the backend ALSO expects % or DKK
export type CommissionUnit = '%' | 'DKK'

// ---------------------------------------------------------------------------
// cm2 price-group helpers
//
// Methods on a cm2 price group (API PriceTypes TYPE1/2/3_CM2) expose their
// size buckets as labels like "100 cm²" (built by the API's GetTemplatesSizes
// from the numeric NAV bucket). The editors key size rows on those labels,
// but the API price engine requires CM2 rules to carry the NUMBER in
// maxSizeCM2 (maxDimensions is only parsed for STANDARD "HxW" rules).
// These helpers translate between the two representations.
// ---------------------------------------------------------------------------

/** API KitMaker.Model.Enums.PriceTypes.STANDARD */
export const STANDARD_PRICE_TYPE = 'Standard'

/** API PriceTypes TYPE1_CM2 / TYPE2_CM2 / TYPE3_CM2 - all take the CM2 branch
 *  in the price engine, so any of them makes a cm2 rule work. */
export const CM2_PRICE_TYPES = ['CM2', 'Type 2 CM2', 'Type 3 CM2'] as const

export function isCm2PriceType(priceType: unknown): priceType is string {
  return (
    typeof priceType === 'string' &&
    CM2_PRICE_TYPES.some((cm2) => cm2 === priceType)
  )
}

/** Parses a cm2 bucket label ("100 cm²", "12,5 cm2") to its numeric value.
 *  Returns null for anything else (e.g. STANDARD "100x100" buckets). */
export function parseCm2BucketValue(
  label: string | null | undefined
): number | null {
  if (!label) return null
  const match = /^\s*(\d+(?:[.,]\d+)?)\s*cm(?:²|2)\s*$/i.exec(label)
  if (!match) return null
  const value = Number(match[1].replace(',', '.'))
  return Number.isFinite(value) ? value : null
}

/** Rebuilds the bucket label the API produces for a cm2 bucket ("100 cm²"). */
export function formatCm2BucketLabel(maxSizeCM2: number): string {
  return `${maxSizeCM2} cm²`
}
